import { useMutation } from '@apollo/client';
import { Button, Meta, theme } from 'aimo-ui';
import {
  IconWrapper,
  Row,
  TextWrapper,
} from 'components/account/paymentMethods/PaymentCardContent';
import { showToast } from 'components/common/CustomToast';
import Icon from 'components/icons/Icon';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { DELETE_WALLET_TOKEN_BY_TOKEN_ID } from 'graphql/mutations/deleteWalletTokenByTokenId';
import useSetDefaultTokenizedPaymentOption from 'hooks/useSetDefaultTokenizedPaymentOption';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  Mutation,
  MutationDeleteWalletTokenByTokenIdArgs,
} from 'types/generatedSchemaTypes';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

const WalletTokenEdit = () => {
  const { t } = useTranslation();
  const {
    customer: { paymentOptions },
    refetchCustomer,
  } = useContext(AccountContext);

  const {
    flags: { tokenized_default_payment },
  } = useContext(FeatureFlagsContext);
  const useTokenizedDefault = showFeatureFlag(tokenized_default_payment);

  const walletToken = paymentOptions?.find(
    (option) => option?.method === 'MOBILEPAY'
  );

  const [removingWalletToken, setRemovingWalletToken] = useState(false);

  const [deleteWalletToken] = useMutation<
    Mutation,
    MutationDeleteWalletTokenByTokenIdArgs
  >(DELETE_WALLET_TOKEN_BY_TOKEN_ID, {
    onCompleted: async () => {
      await refetchCustomer();
      useUiStore.getState().setSheetModal('');
      showToast('success.paymentMethodRemoving', 'success');
    },
    onError: () => {
      showToast('error.paymentMethodRemoving', 'error');
    },
  });

  const handleRemoveWalletToken = () => {
    if (!walletToken?.optionId) {
      showToast('error.paymentMethodRemoving', 'error');
      return;
    }
    setRemovingWalletToken(true);
    deleteWalletToken({
      variables: {
        walletId: walletToken.optionId,
      },
    });
  };

  const { setDefaultTokenizedPaymentOption, loading: setDefaultLoading } =
    useSetDefaultTokenizedPaymentOption();

  const handleSetDefaultButtonChange = () => {
    if (!walletToken?.optionId) {
      return;
    }
    setDefaultTokenizedPaymentOption(walletToken.optionId, walletToken.method);
  };

  return (
    <StyledWrapper mb={0}>
      <StyledWrapper mb={18}>
        {walletToken?.isDefaultTokenizedPaymentOption && (
          <Row {...testIdentifiers('card-default-text')}>
            <IconWrapper>
              <Icon
                name="Info-Filled"
                size="m"
                color={theme.colors.boost.green}
              />
            </IconWrapper>
            <TextWrapper>
              <Meta>{t('paymentMethods.walletTokenDefault')}</Meta>
            </TextWrapper>
          </Row>
        )}
      </StyledWrapper>
      {useTokenizedDefault && !walletToken?.isDefaultTokenizedPaymentOption && (
        <StyledWrapper mb={20}>
          <Button
            loading={setDefaultLoading}
            {...testIdentifiers('payment-methods-set-default-button')}
            buttonType="tertiary"
            text={t('paymentMethods.setAsDefault')}
            size="l"
            disabled={removingWalletToken}
            onPress={handleSetDefaultButtonChange}
          />
        </StyledWrapper>
      )}
      <StyledWrapper mb={25}>
        <Button
          {...testIdentifiers('payment-methods-remove')}
          accessibilityLabel={t('paymentMethods.removeCard')}
          size="l"
          text={t('paymentMethods.removeCard')}
          onPress={handleRemoveWalletToken}
          loading={removingWalletToken}
          disabled={
            removingWalletToken || setDefaultLoading || !walletToken?.optionId
          }
          buttonType="tertiary"
        />
      </StyledWrapper>
    </StyledWrapper>
  );
};

export default WalletTokenEdit;
